import { Fragment, useState } from "react";
import { toast } from "sonner";
import { Plus, Minus, Pencil, Trash2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Button } from "../ui/button";
import { api, yen, errMsg } from "../../lib/api";
import { jstToday } from "../../lib/jst";
import { useI18n } from "../../lib/i18n";

/** Tabel stok produk dikelompokkan per kategori. */
export const StockTable = ({ grouped, isEmpty, onlyNoPhoto, onEdit, onReload, selectable, selected, onToggle }) => {
  const { t } = useI18n();
  const [uploading, setUploading] = useState(false);
  const todayStr = jstToday();

  const adjust = async (p, delta) => {
    const safe = delta < 0 ? -Math.min(p.stock, -delta) : delta;
    if (safe === 0) return;
    await api.post(`/products/${p.id}/stock`, { delta: safe });
    onReload();
  };

  const uploadRowPhoto = async (p, input) => {
    const file = input.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.put(`/products/${p.id}`, { image_url: data.url });
      onReload();
      toast.success(t("photo_saved"));
    } catch (err) {
      toast.error(errMsg(err, "Gagal mengunggah foto"));
    } finally {
      input.value = "";
      setUploading(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {selectable && <TableHead className="w-8" />}
              <TableHead>{t("name")}</TableHead>
              <TableHead>{t("category")}</TableHead>
              <TableHead className="text-right">{t("price")}</TableHead>
              <TableHead className="text-center">{t("stock")}</TableHead>
              <TableHead className="text-right">{t("action")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isEmpty && (
              <TableRow>
                <TableCell
                  colSpan={selectable ? 6 : 5}
                  className="py-8 text-center text-sm text-muted-foreground"
                  data-testid="stok-empty"
                >
                  {onlyNoPhoto ? t("stock_all_ok") : t("no_data")}
                </TableCell>
              </TableRow>
            )}
            {grouped.map(([cat, rows]) => (
              <Fragment key={`grp-${cat}`}>
                <TableRow className="bg-gray-50/80 hover:bg-gray-50/80">
                  <TableCell
                    colSpan={selectable ? 6 : 5}
                    className="py-2 text-xs font-semibold uppercase tracking-wide text-gray-500"
                    data-testid={`category-group-${cat}`}
                  >
                    {cat} · {rows.length}
                  </TableCell>
                </TableRow>
                {rows.map((p) => {
                  const low = p.track_stock && p.stock <= p.low_stock_threshold;
                  return (
                    <TableRow
                      key={p.id}
                      data-testid={`product-row-${p.id}`}
                      className={p.track_stock && p.stock <= 0 ? "bg-red-50/60" : ""}
                    >
                      {selectable && (
                        <TableCell className="w-8">
                          <input
                            type="checkbox"
                            data-testid={`select-menu-${p.id}`}
                            checked={selected?.includes(p.id) || false}
                            onChange={() => onToggle(p.id)}
                            className="h-4 w-4 accent-gray-900"
                          />
                        </TableCell>
                      )}
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-lg bg-gray-100 overflow-hidden shrink-0">
                            {p.image_url && (
                              <img
                                src={p.image_url}
                                alt={p.name}
                                loading="lazy"
                                className="h-full w-full object-cover"
                              />
                            )}
                          </div>
                          <span>{p.name}</span>
                          {(p.images?.length || 0) > 1 && (
                            <span
                              className="text-xs text-muted-foreground"
                              data-testid={`photo-count-${p.id}`}
                            >
                              · {p.images.length} foto
                            </span>
                          )}
                          {p.is_new && (
                            <span
                              data-testid={`stok-new-flag-${p.id}`}
                              className="text-[10px] font-bold text-white bg-primary rounded-full px-2 py-0.5"
                            >
                              NEW
                            </span>
                          )}
                          {p.weight && (
                            <span
                              className="text-xs text-muted-foreground"
                              data-testid={`product-weight-${p.id}`}
                            >
                              · {p.weight}
                            </span>
                          )}
                          {!p.image_url && (
                            <label
                              className="text-xs rounded-full bg-amber-50 border border-amber-200 text-amber-800 px-2 py-0.5 cursor-pointer hover:bg-amber-100"
                              title={t("photo_missing_hint")}
                              data-testid={`no-photo-${p.id}`}
                            >
                              {t("photo_missing")}
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                data-testid={`row-photo-input-${p.id}`}
                                disabled={uploading}
                                onChange={(e) => uploadRowPhoto(p, e.target)}
                              />
                            </label>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{p.category}</TableCell>
                      <TableCell className="text-right num">{yen(p.price)}</TableCell>
                      {!p.track_stock && <TableCell />}
                      {p.track_stock && (
                        <TableCell>
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              size="icon"
                              variant="outline"
                              className="h-8 w-8"
                              data-testid={`stock-dec-${p.id}`}
                              onClick={() => adjust(p, -1)}
                            >
                              <Minus size={14} />
                            </Button>
                            <span
                              className={`num w-20 text-center text-sm ${
                                p.stock <= 0
                                  ? "text-red-600 font-bold"
                                  : low
                                    ? "text-amber-600 font-semibold"
                                    : ""
                              }`}
                            >
                              {p.stock} {p.unit}
                            </span>
                            <Button
                              size="icon"
                              variant="outline"
                              className="h-8 w-8"
                              data-testid={`stock-inc-${p.id}`}
                              onClick={() => adjust(p, 1)}
                            >
                              <Plus size={14} />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                      <TableCell className="text-right">
                        {p.kind === "menu" && (
                          <Button
                            size="sm"
                            variant={p.today_date === todayStr ? "default" : "outline"}
                            className="mr-1 h-8 text-xs"
                            data-testid={`today-toggle-${p.id}`}
                            onClick={async () => {
                              await api.post(`/products/${p.id}/today`, null, {
                                params: { available: p.today_date !== todayStr },
                              });
                              onReload();
                              toast.success(
                                p.today_date === todayStr
                                  ? "Dihapus dari menu hari ini"
                                  : "Ditandai tersedia hari ini"
                              );
                            }}
                          >
                            Hari ini
                          </Button>
                        )}
                        <Button
                          size="icon"
                          variant="ghost"
                          data-testid={`edit-product-${p.id}`}
                          onClick={() => onEdit(p)}
                        >
                          <Pencil size={15} />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="text-destructive"
                          data-testid={`delete-product-${p.id}`}
                          onClick={async () => {
                            await api.delete(`/products/${p.id}`);
                            onReload();
                            toast.success("Produk dihapus");
                          }}
                        >
                          <Trash2 size={15} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
